import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBars, 
  faXmark, 
  faUserCircle,
  faGear,
  faRightFromBracket,
  faChartBar,
  faUsers,
  faCar,
  faBox,
  faPaw,
  faBullhorn,
  faTriangleExclamation,
  faUserGroup
} from '@fortawesome/free-solid-svg-icons';
import closeSession from '../../utils/closeSession';
import { BASE_URL } from '../../utils/constants';
import NotificationBell from '../../common/NotificationBell';

export default function NavigationUser() {
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [userAvatar, setUserAvatar] = useState(null);
  const [userName, setUserName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const userData = JSON.parse(atob(token.split('.')[1]));
        setUserAvatar(userData.avatar);
        setUserName(`${userData.nombre} ${userData.apellido}`);
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Keep page content from being overlapped by the fixed sidebar
  useEffect(() => {
    const padding = isOpen ? '16rem' : '4rem';
    const prev = document.body.style.paddingLeft;
    document.body.style.paddingLeft = padding;
    return () => { document.body.style.paddingLeft = prev; };
  }, [isOpen]);

  const handleListItemClick = (text) => {
    const routes = {
      'Dashboard': '/userHome',
      'Visitas': '/VisitsControl',
      'Pedidos': '/OrdersControl',
      'Paqueteria': '/PackagesControl',
      'Vehículos': '/UserVehicles',
      'Mascotas': '/UserPets',
      'Visitantes Recurrentes': '/userRecurringVisitors',
      'Reporte Visitas': '/VisitsUsrReport',
      'Reporte Paquetería': '/ShipmentsUsrReport',
      'Comunicados': '/userComunicados',
      'Emergencias': '/userEmergencias'
    };
    
    if (routes[text]) {
      navigate(routes[text]);
    }
    setIsOpen(false);
  };

  const handleLogout = () => {
    closeSession();
  };

  const mainMenuItems = [
    { text: 'Dashboard', icon: faChartBar },
    { text: 'Visitas', icon: faUsers },
    { text: 'Pedidos', icon: faBox },
    { text: 'Paqueteria', icon: faBox },
    { text: 'Vehículos', icon: faCar },
    { text: 'Mascotas', icon: faPaw },
    { text: 'Visitantes Recurrentes', icon: faUserGroup }
  ];

  const reportItems = [
    { text: 'Reporte Visitas', icon: faUsers },
    { text: 'Reporte Paquetería', icon: faBox }
  ];

  const comunicadosItems = [
    { text: 'Comunicados', icon: faBullhorn }
  ];

  const emergenciasItems = [
    { text: 'Emergencias', icon: faTriangleExclamation }
  ];

  return (
    <>
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 shadow-lg nav-safe-top">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="text-white hover:bg-indigo-700 p-2 rounded-lg transition-colors duration-200"
              >
                {isOpen ? (
                  <FontAwesomeIcon icon={faXmark} className="h-6 w-6" />
                ) : (
                  <FontAwesomeIcon icon={faBars} className="h-6 w-6" />
                )}
              </button>
              <h1 className="ml-4 text-xl font-bold text-white">VECINO SEGURO</h1>
            </div>

            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <NotificationBell />

              {/* Profile Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 text-white hover:bg-indigo-700 px-3 py-2 rounded-lg transition-colors duration-200"
                >
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center overflow-hidden ring-2 ring-white">
                    {userAvatar ? (
                      <img 
                        src={`${BASE_URL}${userAvatar}`} 
                        alt="Avatar" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faUserCircle} className="h-6 w-6 text-indigo-600" />
                    )}
                  </div>
                  <span className="hidden md:block font-medium">{userName}</span>
                </button>

                {showProfileMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowProfileMenu(false)}></div>
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-50 overflow-hidden">
                      <div className="py-1">
                        <button
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate('/profile');
                          }}
                          className="flex items-center w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <FontAwesomeIcon icon={faGear} className="h-5 w-5 text-indigo-600 mr-3" />
                          Configuración
                        </button>
                        <div className="border-t border-gray-100"></div>
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <FontAwesomeIcon icon={faRightFromBracket} className="h-5 w-5 mr-3" />
                          Salir
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar (collapsible: icons visible when collapsed) */}
      <div className={`fixed sidebar-top-safe left-0 bottom-0 z-40 bg-white shadow-xl transition-all duration-300 ${isOpen ? 'w-64' : 'w-16'} overflow-hidden`}>
        <div className="h-full flex flex-col">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200">
            {isOpen ? (
              <h2 className="text-lg font-semibold text-gray-800">MENU</h2>
            ) : (
              <div className="flex items-center justify-center">
                <span className="text-indigo-600 font-bold">U</span>
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div className="flex-1 overflow-y-auto py-4">
            <nav className="space-y-1 px-3">
              {mainMenuItems.map((item) => {
                return (
                  <button
                    key={item.text}
                    onClick={() => handleListItemClick(item.text)}
                    className="flex items-center w-full px-3 py-3 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg transition-all duration-200 group"
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-indigo-600 group-hover:text-indigo-700" />
                    {isOpen && <span className="ml-3 font-medium">{item.text}</span>}
                  </button>
                );
              })}
            </nav>

            <div className="my-4 border-t border-gray-200"></div>

            {/* Report Items */}
            <nav className="space-y-1 px-3">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                {isOpen ? (
                  'Reportes'
                ) : (
                  <div className="flex items-center justify-center">
                    <span className="text-gray-500 font-semibold">R</span>
                  </div>
                )}
              </div>
              {reportItems.map((item) => {
                return (
                  <button
                    key={item.text}
                    onClick={() => handleListItemClick(item.text)}
                    className="flex items-center w-full px-3 py-3 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg transition-all duration-200 group"
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-indigo-600 group-hover:text-indigo-700" />
                    {isOpen && <span className="ml-3 font-medium">{item.text}</span>}
                  </button>
                );
              })}
            </nav>

            <div className="my-4 border-t border-gray-200"></div>

            {/* Comunicados */}
            <nav className="space-y-1 px-3">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                {isOpen ? 'Comunicados' : (
                  <div className="flex items-center justify-center">
                    <span className="text-gray-500 font-semibold">C</span>
                  </div>
                )}
              </div>
              {comunicadosItems.map((item) => (
                <button key={item.text} onClick={() => handleListItemClick(item.text)}
                  className="flex items-center w-full px-3 py-3 text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 rounded-lg transition-all duration-200 group">
                  <FontAwesomeIcon icon={item.icon} className="h-5 w-5 text-indigo-600 group-hover:text-indigo-700" />
                  {isOpen && <span className="ml-3 font-medium">{item.text}</span>}
                </button>
              ))}
            </nav>

            <div className="my-4 border-t border-gray-200"></div>

            {/* Emergencias */}
            <nav className="space-y-1 px-3">
              <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase">
                {isOpen ? 'Emergencias' : (
                  <div className="flex items-center justify-center">
                    <span className="text-gray-500 font-semibold">E</span>
                  </div>
                )}
              </div>
              {emergenciasItems.map((item) => (
                <button key={item.text} onClick={() => handleListItemClick(item.text)}
                  className="flex items-center w-full px-3 py-3 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all duration-200 group">
                  <FontAwesomeIcon icon={item.icon} className="h-5 w-5" />
                  {isOpen && <span className="ml-3 font-medium">{item.text}</span>}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 sidebar-top-safe"
          onClick={() => setIsOpen(false)}
        ></div>
      )}

      {/* Spacer for top navbar */}
      <div className="h-nav-safe"></div>
    </>
  );
}